<template>
  <div class="data-capture-container">
    <h2>Strength Mapping</h2>
    <p><strong>Time</strong>: 15 minutes</p>
    <p>
      <strong>What to do:</strong>
    </p>
    <p>
      You will map yourself against the 34 Gyre Strengths, identifying which strengths are most
      strongly characteristic of you, and which are least strongly characteristic.
    </p>
    <div v-if="!hasSubmitted" data-test="job-role-data-form">
      <p id="jobTitleLabel">
        But first, please state what your current role is at your organisation?
      </p>
      <form @submit.prevent="onSubmitJobData" class="data-capture-form">
        <input
          class="data-capture data-capture-input"
          type="text"
          placeholder="Job title"
          v-model.trim="jobTitle"
          @focus="clearErrorMessage"
          data-test="job-title-input"
          aria-labelledby="jobTitleLabel"
        />
        <p id="roleDescriptionLabel">Tell us a little bit about your role (optional)</p>
        <textarea
          class="data-capture data-capture-textarea scrollbar"
          type="text"
          v-model.trim="roleDescription"
          ref="textarea"
          placeholder="Brief description"
          @input="onInput"
          @focus="clearErrorMessage"
          data-test="role-description-input"
          aria-labelledby="roleDescriptionLabel"
        />
        <p v-if="roleDescription.length" class="character-count" style="margin-bottom: 10px">
          {{ jobDescriptionCharacterCount }} / 500
        </p>
        <button
          type="submit"
          :disabled="isSubmitting"
          class="btn btn-primary btn-lg"
          data-test="job-form-submit-button"
        >
          Submit
        </button>
      </form>
      <p v-if="submitErrorMessage" class="error-message" data-test="job-submit-error">
        {{ submitErrorMessage }}
      </p>
    </div>
    <div v-else data-test="job-submit-success">
      <div class="center-flex">
        <img src="../../assets/Tick-GIF.gif" alt="success" class="success-img" />
      </div>
      <p>Great! Scroll down to start mapping your Strengths</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import axios, { AxiosError } from "axios";
import { useAxios } from "../../composables/api";
import { UserJobDataTageApiModel } from "../../api-models";
import { csContext } from "../../services/cs-context";
import SessionInstanceState from "../sessions/session-instance-state";

const emit = defineEmits(["on-submit-data"]);

const MAX_JOB_TITLE_LENGTH = 40;
const MAX_JOB_DESCRIPTION_LENGTH = 500;

const jobTitle = ref("");
const roleDescription = ref("");

const isSubmitting = ref(false);
const hasSubmitted = ref(false);
const submitErrorMessage = ref<string | null>(null);

const textarea = ref<HTMLTextAreaElement>();

const exceededJobDescriptionLength = computed(() => {
  return roleDescription.value.length > MAX_JOB_DESCRIPTION_LENGTH;
});

const jobDescriptionCharacterCount = computed(() => {
  return roleDescription.value.length;
});

onBeforeUnmount(() => {
  SessionInstanceState.set("jobTitle", jobTitle.value);
  SessionInstanceState.set("roleDescription", roleDescription.value);
});

onMounted(() => {
  jobTitle.value = SessionInstanceState.get("jobTitle") ?? "";
  roleDescription.value = SessionInstanceState.get("roleDescription") ?? "";
});

function containsOnlyNumbersAndLetters(input: string): boolean {
  const regex = /^[a-zA-Z\d\s]+$/;
  return regex.test(input);
}

function validateJobTitle(): boolean {
  if (!jobTitle.value) {
    submitErrorMessage.value = "Please enter a job title";
    return false;
  }

  if (!containsOnlyNumbersAndLetters(jobTitle.value)) {
    submitErrorMessage.value = "Job title should not contain special characters";
    return false;
  }

  if (jobTitle.value.length > MAX_JOB_TITLE_LENGTH) {
    submitErrorMessage.value = "Job title should be no longer than 40 characters";
    return false;
  }

  if (exceededJobDescriptionLength.value) {
    submitErrorMessage.value = "Role description should be maximum 500 characters";
    return false;
  }

  return true;
}

async function onSubmitJobData(): Promise<void> {
  submitErrorMessage.value = null;
  isSubmitting.value = true;

  const isJobTitleValid = validateJobTitle();

  if (!isJobTitleValid) {
    isSubmitting.value = false;
    return;
  }

  const defaultErrorMessage =
    "There was an error submitting this information, please refresh the page and try again.";

  try {
    await axios.post<UserJobDataTageApiModel>("/api/usertagdata/job", {
      userId: csContext.user.id,
      jobTitle: jobTitle.value,
      roleDescription: roleDescription.value,
      taggedTeams: [],
    } as UserJobDataTageApiModel);
    isSubmitting.value = false;
    hasSubmitted.value = true;
    emit("on-submit-data", "preStrengthMapping");
  } catch (err) {
    const error = err as AxiosError;
    const statusCode = error.response?.status;
    isSubmitting.value = false;
    submitErrorMessage.value = statusCode?.toString().startsWith("4")
      ? error.response?.data
      : defaultErrorMessage;
    hasSubmitted.value = false;
  }
}

function clearErrorMessage(): void {
  submitErrorMessage.value = null;
}

const inputHeight = 90;
const MAX_HEIGHT = 200;
let previousScrollHeight: number;

function updateTextAreaHeight(): void {
  const textareaElement = textarea.value as HTMLTextAreaElement;
  textareaElement.style.height = `${inputHeight}px`;

  const { scrollHeight } = textareaElement;

  if (scrollHeight > MAX_HEIGHT) {
    textareaElement.style.height = `${MAX_HEIGHT}px`;
    return;
  }

  textareaElement.style.height =
    scrollHeight > inputHeight ? `${scrollHeight}px` : `${inputHeight}px`;
}

function onInput(): void {
  if (previousScrollHeight !== textarea.value?.scrollHeight) {
    previousScrollHeight = textarea.value?.scrollHeight ?? 0;
    updateTextAreaHeight();
  }
}

function isNotEmptyString(str: string | null | undefined): boolean {
  return str !== null && str !== undefined && str.trim() !== "";
}

const { data } = useAxios<UserJobDataTageApiModel>("/api/usertagdata/getjobdata");

watchEffect(() => {
  if (!hasSubmitted.value && isNotEmptyString(data.value?.jobTitle)) {
    hasSubmitted.value = true;
    emit("on-submit-data", "preStrengthMapping");
  }
});

onMounted(() => {
  updateTextAreaHeight();
  previousScrollHeight = textarea.value?.scrollHeight ?? 0;
});
</script>

<style scoped lang="less">
@import "../../styles/cms-data-capture/data-capture.less";
</style>
